import React, { type FunctionComponent } from 'react';

import { MenuItem, ListItemText, Checkbox, Table, TableRow, TableCell, TableBody } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import AdminSettingsContainer from './AdminSettingsContainer';
import Select from '../../components/forms/Select';
import ClassLogoForHeaderFull from '../../icons/ClassLogoForHeaderFull';

export const SETTINGS_HEADER_HEIGHT = '80px';

const SharedStyles = css`
    height: ${SETTINGS_HEADER_HEIGHT};
    background-color: #ffffff;
    border-bottom: 1px solid #d8d8d8;
`;

const StyledImageContainer = styled.div`
    ${SharedStyles};
    grid-row: 1 / 2;
    grid-column: 1 / 2;
    display: flex;
    align-items: center;

    padding: 0 2rem;
`;

const StyledHeaderTextContainer = styled.div`
    ${SharedStyles};
    grid-row: 1 / 2;
    grid-column: 2 / 3;
    display: flex;
    align-items: center;
    padding: 0 1rem;
`;

/* TODO: Replace this with class or theme `Title L` */
const StyledHeaderText = styled.span`
    font-family: Lato;
    font-size: 34px;
    font-style: normal;
    font-weight: 400;
    line-height: 40px;
    letter-spacing: 0px;
    text-align: left;
`;

/* TODO: Replace this with class or theme `Title L` */
const StyledRightHeader = styled.span`
    font-family: Lato;
    font-size: 34px;
    font-style: normal;
    font-weight: 400;
    line-height: 40px;
    letter-spacing: 0px;
    text-align: right;
    position: absolute;
    right: 24px;
    top: 16px;
`;

// eslint-disable-next-line arrow-body-style
const PortalHeader: FunctionComponent = () => {
    const { t } = useTranslation('portal');
    const { adminSettings } = useContainer(AdminSettingsContainer);
    const {
        isSuperAdmin,
        isAdmin,
        isInstructor,
        setAuthMode,
        authMode,
        setShowDemoContent,
        showDemoContent,
        hasCustomerSupportMode
    } = useContainer(AdminAuthContainer);
    const supportedAuthModes: string[] = [];
    if (isSuperAdmin) {
        if (adminSettings.featureSettings.dataPortalAdmin) {
            supportedAuthModes.push('admin');
        }
        if (adminSettings.featureSettings.dataPortalInstructor) {
            supportedAuthModes.push('instructor');
        }
        if (adminSettings.featureSettings.dataPortalStudent) {
            supportedAuthModes.push('student');
        }
        if (adminSettings.featureSettings.dataPortalCustomerSupport) {
            supportedAuthModes.push('customerSupport');
        }
        if (adminSettings.featureSettings.dataPortalBusinessAnalyst) {
            supportedAuthModes.push('customerBusiness');
        }
        if (adminSettings.featureSettings.dataPortalClassBusinessAnalyst) {
            supportedAuthModes.push('classBusiness');
        }
    } else if (isAdmin) {
        if (adminSettings.featureSettings.dataPortalAdmin) {
            supportedAuthModes.push('admin');
        }
        if (adminSettings.featureSettings.dataPortalInstructor) {
            supportedAuthModes.push('instructor');
        }
        if (adminSettings.featureSettings.dataPortalStudent) {
            supportedAuthModes.push('student');
        }
        if (adminSettings.featureSettings.dataPortalBusinessAnalyst) {
            supportedAuthModes.push('customerBusiness');
        }
    } else if (isInstructor) {
        if (adminSettings.featureSettings.dataPortalInstructor) {
            supportedAuthModes.push('instructor');
        }
        if (adminSettings.featureSettings.dataPortalStudent) {
            supportedAuthModes.push('student');
        }
    } else if (adminSettings.featureSettings.dataPortalStudent) {
        supportedAuthModes.push('student');
    }

    if (!supportedAuthModes.includes('customerSupport') && hasCustomerSupportMode) {
        supportedAuthModes.push('customerSupport');
    }
    const handleAuthMode = (val: string) => {
        setAuthMode(val);
    };
    const handleAuthModeChange = (event) => {
        window.localStorage.setItem('authMode', event.target.value);
        handleAuthMode(event.target.value);
    };

    const handleShowDemoContent = (val: boolean) => {
        window.localStorage.setItem('showDemoContent', String(val));
        setShowDemoContent(val);
    };
    const handleShowDemoContentChange = (event) => {
        handleShowDemoContent(event.target.checked);
    };

    return (
        <>
            <StyledImageContainer>
                <ClassLogoForHeaderFull />
            </StyledImageContainer>
            <StyledHeaderTextContainer>
                <StyledHeaderText>{t('header.settings')}</StyledHeaderText>
            </StyledHeaderTextContainer>
            <StyledRightHeader>
                <Table style={{ width: '100%', padding: '0px', margin: '0px', position: 'relative', top: '-12px' }}>
                    <TableBody style={{ width: '100%', padding: '0px', margin: '0px' }}>
                        <TableRow style={{ width: '100%', padding: '0px', margin: '0px' }}>
                            <TableCell style={{ paddingLeft: '0px', borderBottom: 'none' }}>
                                {authMode === 'customerSupport' && (
                                    <ListItemText
                                        primary={t('show-demo-content')}
                                        style={{ display: 'inline-block' }}
                                    />
                                )}
                                {authMode === 'customerSupport' && (
                                    <Checkbox
                                        onChange={(event) => handleShowDemoContentChange(event)}
                                        checked={showDemoContent}
                                        style={{ display: 'inline-block' }}
                                    ></Checkbox>
                                )}
                            </TableCell>
                            <TableCell style={{ borderBottom: 'none' }}>
                                <div style={{ position: 'relative', top: '-3px' }}>
                                    <Select
                                        name="authMode"
                                        label={t('Dashboard Mode')}
                                        onChange={handleAuthModeChange}
                                        value={authMode}
                                        style={{ marginLeft: '12px', position: 'relative' }}
                                    >
                                        {supportedAuthModes.includes('admin') && (
                                            <MenuItem value="admin">{t('admin')}</MenuItem>
                                        )}
                                        {supportedAuthModes.includes('instructor') && (
                                            <MenuItem value="instructor">{t('instructor')}</MenuItem>
                                        )}
                                        {supportedAuthModes.includes('student') && (
                                            <MenuItem value="student">{t('student')}</MenuItem>
                                        )}
                                        {supportedAuthModes.includes('customerSupport') && (
                                            <MenuItem value="customerSupport">{t('customerSupport')}</MenuItem>
                                        )}
                                        {supportedAuthModes.includes('customerBusiness') && (
                                            <MenuItem value="customerBusiness">{t('customerBusiness')}</MenuItem>
                                        )}
                                        {supportedAuthModes.includes('classBusiness') && (
                                            <MenuItem value="classBusiness">{t('classBusiness')}</MenuItem>
                                        )}
                                    </Select>
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </StyledRightHeader>
        </>
    );
};

export default PortalHeader;
